import { ReactQueryStaleTime } from '@openx/constants/react-query.ts';
import type { CadenceType, MonetizingApi } from '@openx/types/monetizing.ts';
import { QueryKeys } from '@openx/utils/api/queryKeys.ts';
import { useQuery } from '@tanstack/react-query';
import { GetMonetizingDocument, type GetMonetizingQuery } from '../../../graphql/query/GetMonetizing.generated.tsx';
import { getGraphQLRequestFromClient } from '../../../modules/graphql/types.ts';

export type GetMonetizing = Omit<GetMonetizingQuery['getMonetizing'], '__typename'> & {
  cadences: Array<Omit<NonNullable<GetMonetizingQuery['getMonetizing']>['cadences'][number], '__typename'>>;
};

const keysToLowerCase = ['budget_type', 'pacing_mode'];

export const lowerCaseMonetizingValues = (data: GetMonetizing | undefined): MonetizingApi | undefined => {
  if (!data) {
    return undefined;
  }

  const newData: GetMonetizing = { ...data };

  keysToLowerCase.forEach(key => {
    if (newData[key] && typeof newData[key] === 'string') {
      newData[key] = newData[key].toLowerCase();
    }
  });

  if ('cadences' in newData && newData?.cadences?.length) {
    (newData.cadences as unknown as {
      budget: number;
      type: CadenceType;
    }[]) = newData.cadences.map(
      cadence =>
        ({
          ...cadence,
          type: `${cadence?.type}`.toLowerCase(),
        }) as {
          budget: number;
          type: CadenceType;
        }
    );
  }

  return newData as unknown as MonetizingApi;
};

export const useMonetizingFetch = (
  monetizingUid?: string | null,
  enabled = true
): {
  isLoading: boolean;
  monetizing: MonetizingApi | undefined;
} => {
  const variables = { monetizing_uid: monetizingUid || '' };
  const { data, isLoading } = useQuery({
    queryKey: [QueryKeys.MONETIZING, variables],
    queryFn: ({ client }) => getGraphQLRequestFromClient(client)(GetMonetizingDocument, variables),
    enabled: !!(monetizingUid && enabled),
    staleTime: ReactQueryStaleTime.MINUTES_15,
  });

  return { isLoading, monetizing: lowerCaseMonetizingValues(data?.getMonetizing as GetMonetizing) };
};
